'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { ThumbsUp, ThumbsDown, Zap, X, Share2, Check, Timer } from 'lucide-react';
import { Post } from '@/app/components/PostFeed';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import SupportSection from '@/app/components/SupportSection';
import TopSupporters from '@/app/components/TopSupporters';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL ?? 'https://sayit3452.builtwithrocket.new';

interface PostCardProps {
  post: Post;
  onVote: (postId: string, type: 'agree' | 'disagree') => void;
  onSupportUpdate: (postId: string, newSupportCount: number, newAgreeCount: number) => void;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function useCountdown(expiresAt: string | null): string | null {
  const [remaining, setRemaining] = useState<string | null>(null);

  useEffect(() => {
    if (!expiresAt) return;

    const calc = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setRemaining(null);
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(
        `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      );
    };

    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  return remaining;
}

export default function PostCard({ post, onVote, onSupportUpdate }: PostCardProps) {
  const router = useRouter();
  const [showUpsell, setShowUpsell] = useState(true);
  const [copied, setCopied] = useState(false);
  const [topSupportersKey, setTopSupportersKey] = useState(0);
  const countdown = useCountdown(post.boost ? post.boostExpiresAt : null);

  const isWinning = post.agreeCount > post.disagreeCount && post.agreeCount > 0;
  const isLosing = post.disagreeCount > post.agreeCount && post.disagreeCount > 0;
  const isClose = !isWinning && !isLosing && (post.agreeCount > 0 || post.disagreeCount > 0) && Math.abs(post.agreeCount - post.disagreeCount) <= 2;

  const handleVote = (type: 'agree' | 'disagree') => {
    onVote(post.id, type);
  };

  const handleShare = () => {
    const url = post.slug
      ? `${SITE_URL}/p/${post.slug}`
      : SITE_URL;
    const shareText = `I'm calling this.\n\n${post.content}\n\nProve me wrong: ${url}`;
    navigator.clipboard.writeText(shareText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleTopSupportersRefresh = useCallback(() => {
    setTopSupportersKey(k => k + 1);
  }, []);

  return (
    <div
      className={`bg-card rounded-xl border shadow-sm transition-all duration-200 ${
        post.boost
          ? 'border-yellow-400 shadow-yellow-100 bg-yellow-50/30'
          : 'border-border'
      }`}
    >
      {/* Boost badge + countdown */}
      {post.boost && (
        <div className="px-4 pt-3 pb-0 flex items-center justify-between">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full">
            <Zap size={11} />
            Boosted
          </span>
          {countdown ? (
            <span className="inline-flex items-center gap-1 text-xs font-mono font-semibold text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full">
              <Timer size={11} />
              {countdown}
            </span>
          ) : post.boostExpiresAt ? (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              Expired
            </span>
          ) : null}
        </div>
      )}

      <div className="p-4">
        {/* Meta */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-muted-foreground">{formatRelativeTime(post.createdAt)}</span>
          {post.doubleVote && (
            <span className="text-xs font-semibold text-primary bg-accent px-2 py-0.5 rounded-full">
              2× Votes
            </span>
          )}
        </div>

        {/* Content — clickable link to post page */}
        {post.slug ? (
          <Link href={`/p/${post.slug}`} className="block text-sm text-foreground leading-relaxed mb-4 hover:text-primary transition-colors">
            {post.content}
          </Link>
        ) : (
          <p className="text-sm text-foreground leading-relaxed mb-4">{post.content}</p>
        )}

        {/* Vote + Share buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleVote('agree')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95 ${
              post.userVote === 'agree' ? 'bg-green-500 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'
            }`}
          >
            <ThumbsUp size={14} />
            <span className="tabular-nums">{post.agreeCount}</span>
            <span className="text-xs font-normal">They're right</span>
          </button>

          <button
            onClick={() => handleVote('disagree')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95 ${
              post.userVote === 'disagree' ? 'bg-red-500 text-white' : 'bg-red-50 text-red-600 hover:bg-red-100'
            }`}
          >
            <ThumbsDown size={14} />
            <span className="tabular-nums">{post.disagreeCount}</span>
            <span className="text-xs font-normal">No way</span>
          </button>

          <button
            onClick={handleShare}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 active:scale-95"
          >
            {copied ? <Check size={14} className="text-green-500" /> : <Share2 size={14} />}
            <span className="text-xs font-normal">{copied ? 'Copied!' : 'Prove them wrong'}</span>
          </button>
        </div>

        {/* Winning / Losing state */}
        {isWinning && (
          <div className="mt-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-xs font-bold text-green-800">You're ahead. Don't lose it.</p>
          </div>
        )}
        {isLosing && (
          <div className="mt-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs font-bold text-red-700">They're beating you. Do something.</p>
          </div>
        )}
        {isClose && (
          <div className="mt-2 px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs font-bold text-yellow-800">This is close. Every move matters.</p>
          </div>
        )}

        {/* Vote upsell */}
        {showUpsell && (
          <div className="mt-3 flex items-center justify-between bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
            <p className="text-xs text-indigo-700 font-medium">
              ⚡ Push to top — get seen now
            </p>
            <div className="flex items-center gap-2 ml-3">
              <button
                onClick={() => { setShowUpsell(false); router.push(`/boost-payment-screen?postId=${post.id}`); }}
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors whitespace-nowrap"
              >
                Push to top
              </button>
              <button onClick={() => setShowUpsell(false)} className="text-muted-foreground hover:text-foreground transition-colors">
                <X size={13} />
              </button>
            </div>
          </div>
        )}

        {/* Top Supporters */}
        <TopSupporters postId={post.id} refreshKey={topSupportersKey} />

        {/* Support section */}
        <SupportSection
          postId={post.id}
          supportCount={post.supportCount}
          onSupportSuccess={(newSupportCount, newAgreeCount) =>
            onSupportUpdate(post.id, newSupportCount, newAgreeCount)
          }
          onTopSupportersRefresh={handleTopSupportersRefresh}
        />
      </div>
    </div>
  );
}